import { isAxiosError } from "axios";
import { delete_cookie, set_cookie } from "../../config/cookie";
import { AppDispatch } from "../../store";
import { setLoading, stopLoading } from "../../store/slices/loading";
import {
  ILogin,
  Register,
  Verify,
  VerifyResponse,
} from "../../store/types/auth";
import axiosInstance from "../../utility/axiosInstance";
import { toast } from "sonner";
import { setMessage } from "../../store/slices/message";

// Get logged in user
type GetUser = (dispatch: any, id: string | undefined) => Promise<void>;

export const getUser: GetUser = async (
  dispatch: any,
  id: string | undefined,
) => {
  dispatch(setLoading());
  try {
    const response = await axiosInstance.get(`/organizations/${id}`);

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
    }
  }
  dispatch(setLoading());
};

// Login user
export const auth_user = async (
  formData: ILogin,
  dispatch: AppDispatch,
  navigate: any,
): Promise<string | void> => {
  dispatch(setLoading());

  try {
    const response = await axiosInstance.post("/auth/admin/login", formData);
    const { data } = response.data;

    // Store token and admin data
    set_cookie("@serial", data?.token);
    localStorage.setItem("@token", data.token);
    localStorage.setItem("@admin", JSON.stringify(data.admin));

    // Don't store entire response in localStorage to avoid duplication
    localStorage.setItem("loginData", JSON.stringify(response.data));

    return data.token;
  } catch (error) {
    if (isAxiosError(error)) {
      toast.error(error?.response?.data?.message);
    } else {
      toast.error("Please check your Internet.");
    }
  } finally {
    dispatch(stopLoading());
  }
};
// export const auth_user = async (
//   formData: ILogin,
//   dispatch: AppDispatch,
//   navigate: any
// ) => {
//   dispatch(setLoading());
//   try {
//     const response = await axiosInstance.post("/auth/admin/login", formData);
//     localStorage.setItem("loginData", JSON.stringify(response.data));

//     const { data } = response.data;
//     console.log("Respnse:", data.data);
//     // set_cookie("@serial", data?.token);
//     // localStorage.setItem("@serial", data.serial);
//     // Store token and admin data
//     set_cookie("@serial", data?.token);
//     localStorage.setItem("@token", data.token);
//     localStorage.setItem("@admin", JSON.stringify(data.admin));
//     // navigate(`/`);
//     return data.token;
//   } catch (error) {
//     if (isAxiosError(error)) {
//       toast.error(error?.response?.data?.message);
//     } else {
//       toast.error("Please check your Internet.");
//     }
//   }
//   dispatch(stopLoading());
// };

export const logout_user = async (navigate?: any) => {
  localStorage.removeItem("@serial");
  delete_cookie("@serial");
  navigate("/login");
};

// Register
type registerType = (dispatch: any, formData: Register) => Promise<any>;
export const register: registerType = async (dispatch, formData) => {
  dispatch(setLoading());
  try {
    const response = await axiosInstance.post("/organizations", formData);

    // "warning" status means email already registered — return without toast
    // so the UI can show a modal with the "Resend Onboarding Link" action
    if (
      response.data?.status === "warning" ||
      response.data?.message === "This account already exist!"
    ) {
      return response.data;
    } else {
      toast.success(response?.data?.message);
      dispatch(
        setMessage({
          type: "success",
          status: "success",
          message: response?.data?.message,
          title: "Successful",
        }),
      );
      return response.data;
    }
  } catch (error) {
    if (isAxiosError(error)) {
      toast.error(error?.response?.data?.message);
      dispatch(
        setMessage({
          type: "error",
          status: "error",
          message: error?.response?.data?.message,
          title: "Failed",
        }),
      );
      return error.response?.data;
    } else {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Registration failed",
      };
    }
  } finally {
    dispatch(stopLoading());
  }
};

// Verify OTP
type verifyType = (dispatch: any, formData: Verify) => Promise<VerifyResponse>;
export const verifyOtp: verifyType = async (dispatch, formData) => {
  dispatch(setLoading());

  try {
    const response = await axiosInstance.post(
      "/organizations/verify",
      formData,
    );

    if (response.data?.message === "This account already verified!") {
      toast.info(response.data?.message);
      dispatch(
        setMessage({
          type: "info",
          message: response.data?.message,
          title: "Already Verified",
          status: "info",
        }),
      );
      return {
        success: true,
        message: response.data?.message,
        data: response.data,
      };
    } else {
      toast.success(response?.data?.message);
      dispatch(
        setMessage({
          type: "success",
          status: "success",
          message: response?.data?.message,
          title: "Verification Successful",
        }),
      );
      return {
        success: true,
        message: response?.data?.message,
        data: response.data,
      };
    }
  } catch (error) {
    let errorMessage = "Verification failed. Please try again.";

    if (isAxiosError(error)) {
      errorMessage = error?.response?.data?.message || errorMessage;
      toast.error(errorMessage);
      dispatch(
        setMessage({
          type: "error",
          status: "error",
          message: errorMessage,
          title: "Verification Failed",
        }),
      );
    } else {
      toast.error(errorMessage);
      dispatch(
        setMessage({
          type: "error",
          status: "error",
          message: errorMessage,
          title: "Verification Failed",
        }),
      );
    }

    // Re-throw the error so the component can handle it
    throw new Error(errorMessage);
  } finally {
    dispatch(stopLoading());
  }
};

// Forgot Password
export const forgot_password = async (
  email: string,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setLoading());

  try {
    const response = await axiosInstance.post("/auth/forgot-password", {
      email,
    });

    toast.success(
      response?.data?.message || "Password reset link sent to your email",
    );
    return true;
  } catch (error) {
    if (isAxiosError(error)) {
      toast.error(
        error?.response?.data?.message || "Failed to send reset link",
      );
    } else {
      toast.error("Please check your Internet.");
    }
    return false;
  } finally {
    dispatch(stopLoading());
  }
};

// Reset Password
export const reset_password = async (
  formData: { encoded: string; newPassword: string },
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setLoading());

  try {
    const response = await axiosInstance.post("/auth/reset-password", formData);

    toast.success(response?.data?.message || "Password reset successful");
    return true;
  } catch (error) {
    if (isAxiosError(error)) {
      toast.error(error?.response?.data?.message || "Failed to reset password");
    } else {
      toast.error("Please check your Internet.");
    }
    return false;
  } finally {
    dispatch(stopLoading());
  }
};

// Change Password
export const change_password = async (
  formData: { oldPassword: string; newPassword: string },
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setLoading());

  try {
    const response = await axiosInstance.post(
      "/auth/change-password",
      formData,
    );

    toast.success(response?.data?.message || "Password changed successfully");
    return true;
  } catch (error) {
    if (isAxiosError(error)) {
      toast.error(
        error?.response?.data?.message || "Failed to change password",
      );
    } else {
      toast.error("Please check your Internet.");
    }
    return false;
  } finally {
    dispatch(stopLoading());
  }
};
